<script lang="ts">
	import { Spinner } from "flowbite-svelte";
	import { getContext, type ComponentEvents } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { Point } from "#gen/bundles/v1/common_pb.js";
	import type { StaticAssetClient } from "#lib/api/static-assets.js";
	import Dropzone from "#lib/components/Dropzone.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import { UPLOADABLE_IMAGE_FILE_TYPES } from "#lib/components/images/constants.js";
	import FileManagerDialog from "#lib/components/images/file-management/FileManagerDialog.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { validateUploadFile } from "#lib/components/images/utils.js";
	import type { Writable } from "svelte/store";

	interface $$Events {
		change: CustomEvent<ImagePickerChangeEvent>;
	}

	const staticAssetClient: StaticAssetClient = getContext("staticAssetClient");
	const lastError: Writable<unknown> = getContext("lastError");

	export let dropzoneClass = "";
	export let aspectRatio: number | undefined = undefined;
	export let initialAspectRatio: number | undefined = undefined;
	export let caption: string | undefined = undefined;
	export let shape: "circle" | "rectangle" | undefined = undefined;
	export let finalWidth: number | undefined = undefined;
	export let finalHeight: number | undefined = undefined;
	export let imageTypeCaption = "image";
	export let fileTypes: string[] = UPLOADABLE_IMAGE_FILE_TYPES;
	export let disabled = false;
	export let allowFocalPointSelection = false;
	export let fromOrgAdmin = false;
	export let showAspectRatioPicker = false;
	export let preferFreeformAspectRatio = false;

	let loading = false;
	let openModal = false;
	let value: string | undefined;
	const focalPoint: Point | undefined = undefined;
	let contentType: string | undefined;

	$: if (!openModal) {
		reset();
	}

	function click(e: MouseEvent) {
		e.preventDefault();
		openModal = true;
	}

	async function onFileDropped(e: ComponentEvents<Dropzone>["change"]) {
		loading = true;
		try {
			await validateUploadFile(e.detail);
			const { url } = await staticAssetClient.upload(e.detail);
			value = url;
			contentType = e.detail.type;
			openModal = true;
		} catch (e) {
			$lastError = e;
		} finally {
			loading = false;
		}
	}

	function reset() {
		value = undefined;
		$lastError = undefined;
	}
</script>

<Dropzone on:click={click} on:change={onFileDropped} bind:lastError={$lastError} {disabled} dropzoneClass={twMerge(dropzoneClass, disabled ? "cursor-default hover:bg-gray-50" : "")} fileTypes={fileTypes.join(",")}>
	{#if loading}
		<div class="flex h-full w-full items-center justify-center bg-gray-50">
			<Spinner />
		</div>
	{:else}
		<div class="flex flex-col items-center justify-center">
			<Icon name="cloud-arrow-up" class="opacity-40" />
			<slot />
		</div>
	{/if}
</Dropzone>

{#if openModal}
	<FileManagerDialog bind:open={openModal} {focalPoint} on:change {imageTypeCaption} {contentType} {value} {aspectRatio} {initialAspectRatio} {shape} {caption} {finalWidth} {finalHeight} {fileTypes} {allowFocalPointSelection} {fromOrgAdmin} {showAspectRatioPicker} {preferFreeformAspectRatio} />
{/if}
